import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LANGUAGES, useI18n } from "@/lib/i18n";

export default function Register() {
  const { register } = useAuth();
  const { setLang } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({
    garage_name: "", owner_name: "", email: "", password: "", phone: "", language: "en",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      // Persist language client-side so translation kicks in immediately
      if (form.language && form.language !== "en") setLang(form.language);
      localStorage.setItem("fender_lang_picked", "1");
      toast.success("Garage created — welcome to Fender");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-honeydew text-deep-space p-6">
      <div className="w-full max-w-lg bg-white border border-frosted p-8 shadow-sm">
        <Link to="/" className="inline-block mb-6" data-testid="register-logo">
          <img src="/fender-logo-lockup.png" alt="Fender by SparesHub" className="h-10 w-auto" width="720" height="208" />
        </Link>
        <h1 className="text-3xl font-display font-bold mb-1 text-deep-space">Sign Up</h1>
        <p className="text-sm text-steel mb-6">Create your workshop account in minutes. No credit card needed.</p>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-steel/80">Garage Name</Label>
            <Input data-testid="register-garage-input" required value={form.garage_name}
              onChange={(e) => set("garage_name", e.target.value)}
              className="mt-1 h-11 rounded-md bg-white border-frosted text-deep-space" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-steel/80">Owner Name</Label>
            <Input data-testid="register-owner-input" required value={form.owner_name}
              onChange={(e) => set("owner_name", e.target.value)}
              className="mt-1 h-11 rounded-md bg-white border-frosted text-deep-space" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono text-steel/80">Email</Label>
              <Input data-testid="register-email-input" type="email" required value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-1 h-11 rounded-md bg-white border-frosted text-deep-space" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono text-steel/80">Phone</Label>
              <Input data-testid="register-phone-input" value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="mt-1 h-11 rounded-md bg-white border-frosted text-deep-space" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-steel/80">Password (min 6)</Label>
            <Input data-testid="register-password-input" type="password" required minLength={6}
              value={form.password} onChange={(e) => set("password", e.target.value)}
              className="mt-1 h-11 rounded-md bg-white border-frosted text-deep-space" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-mono text-steel/80">Preferred Language</Label>
            <select data-testid="register-language-select" value={form.language}
              onChange={(e) => set("language", e.target.value)}
              className="mt-1 h-11 w-full rounded-md bg-white border border-frosted text-deep-space px-3 outline-none focus:border-steel">
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label}{l.code === "en" ? "" : ` — ${l.native}`}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-steel/70">You can change this any time in Settings.</p>
          </div>
          <Button data-testid="register-submit-btn" disabled={loading}
            className="w-full h-11 btn-primary-brand rounded-md text-white font-semibold">
            {loading ? "Creating…" : "Create garage"}
          </Button>
        </form>

        <p className="mt-6 text-sm text-steel/80">
          Already have an account?{" "}
          <Link to="/login" className="text-strawberry hover:underline" data-testid="register-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
